import { useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router';
import { Box, Card, EmptySearchResult, Layout, Page } from '@shopify/polaris';
import LoadingPanel from '#client/components/common/LoadingPanel/LoadingPanel';
import { formatDateToUTC, handleSort } from '#client/utils/helpers';
import { useQuery } from '@tanstack/react-query';

// Children components
import PurchaseOrderASNDetails from './components/PurchaseOrderASNDetails';
import ReceivingDocumentsTable from './components/ReceivingDocumentsTable';
import CartonsTable from './components/CartonsTable';
import PurchaseOrderBreadcrumbs from '../PurchaseOrderBreadcrumbs';

import PURCHASE_ORDER_STRINGS from '#client/pages/PurchaseOrders/constants/purchaseOrders.en.json';

/**
 * Simplified PurchaseOrderAsn component that fetches pre-processed data from optimized backend endpoints
 */
const PurchaseOrderAsn = () => {
    const location = useLocation();
    const { asnId, poId } = useParams();
    const navigate = useNavigate();
    const passedState = location?.state;

    const [activeModals, setActiveModals] = useState({});

    // Separate state for sorting
    const [sortState, setSortState] = useState({
        carton: { direction: 'none', columnIndex: null },
        receivingDocument: { direction: 'none', columnIndex: null },
    });

    const toggleModal = useCallback((index) => {
        setActiveModals((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    }, []);

    // Optimized API URLs
    const apiUrl = useMemo(() => `${import.meta.env.VITE_API_URL}/api/app/purchase-orders/${poId}`, [poId]);

    const { data, isLoading } = useQuery({
        queryKey: ['purchase-order', poId],
        enabled: !!poId,
        queryFn: async () => {
            const response = await axios.get(apiUrl);
            return response.data.data;
        },
    });

    const purchaseOrder = data?.purchaseOrder || null;
    const productData = data?.productData || null;

    const asn = useMemo(() => {
        if (!purchaseOrder?.advancedShippingNotices || !asnId) return null;
        return purchaseOrder.advancedShippingNotices.find((notice) => String(notice.id) === String(asnId));
    }, [purchaseOrder, asnId]);

    const parentData = useMemo(() => {
        if (!asn || !purchaseOrder) return null;

        let totalCartons = 0;
        if (asn.shipments) {
            for (let i = 0; i < asn.shipments.length; i++) {
                const shipment = asn.shipments[i];
                if (shipment.cartons) {
                    totalCartons += shipment.cartons.length;
                }
            }
        }

        return {
            ...asn,
            purchaseOrder,
            totalCartons,
            netSuiteHumanPoNumber: purchaseOrder.netSuiteHumanPoNumber,
            expectedDate: formatDateToUTC(asn?.shipments?.[0]?.expectedDate),
            asnNetsuiteId: asn?.netsuiteId,
            navigateFromASN: true,
            asnIdValue: purchaseOrder?.netsuiteId,
        };
    }, [asn, purchaseOrder]);

    const baseCartonRows = useMemo(() => {
        if (!asn?.shipments) return [];
        return asn.shipments.flatMap((shipment) => shipment.cartons || []);
    }, [asn]);

    const baseReceivingDocumentRows = useMemo(() => asn?.receivingDocuments || [], [asn]);

    // Memoized sort handlers
    const cartonSortHandlers = useMemo(
        () => ({
            sortDirection: sortState.carton.direction,
            sortColumnIndex: sortState.carton.columnIndex,
            handleSort: (columnIndex, direction) => {
                setSortState((prev) => ({
                    ...prev,
                    carton: { direction, columnIndex },
                }));
            },
        }),
        [sortState.carton.direction, sortState.carton.columnIndex]
    );

    const receivingDocumentSortHandlers = useMemo(
        () => ({
            sortDirection: sortState.receivingDocument.direction,
            sortColumnIndex: sortState.receivingDocument.columnIndex,
            handleSort: (columnIndex, direction) => {
                setSortState((prev) => ({
                    ...prev,
                    receivingDocument: { direction, columnIndex },
                }));
            },
        }),
        [sortState.receivingDocument.direction, sortState.receivingDocument.columnIndex]
    );

    const cartonRows = useMemo(() => {
        if (sortState.carton.direction === 'none' || sortState.carton.columnIndex == null) {
            return baseCartonRows;
        }
        return handleSort(baseCartonRows, sortState.carton.columnIndex, sortState.carton.direction, ['cartonNumber', 'shipmentCarrier', 'shipmentTrackingNumber', 'quantityExpected']);
    }, [baseCartonRows, sortState.carton.columnIndex, sortState.carton.direction]);

    const receivingDocumentRows = useMemo(() => {
        if (sortState.receivingDocument.direction === 'none' || sortState.receivingDocument.columnIndex == null) {
            return baseReceivingDocumentRows;
        }
        return handleSort(baseReceivingDocumentRows, sortState.receivingDocument.columnIndex, sortState.receivingDocument.direction, [
            'id',
            'status',
            'createdAt',
            'updatedAt',
            'totalCartonLines',
            'quantityReceived',
        ]);
    }, [baseReceivingDocumentRows, sortState.receivingDocument.columnIndex, sortState.receivingDocument.direction]);

    const skuToProduct = useMemo(() => {
        return productData?.reduce((acc, product) => {
            acc[product.sku] = product;
            return acc;
        }, {});
    }, [productData]);

    if (isLoading) {
        return <LoadingPanel />;
    }

    return (
        <>
            <PurchaseOrderBreadcrumbs nsId={parentData?.netSuiteHumanPoNumber} poId={poId} asnId={asnId} asnIdValue={parentData?.asnNetsuiteId} />
            <Page title={PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_ASN.TITLE} fullWidth>
                <Layout>
                    {poId ? (
                        <>
                            {/* Purchase Order Details Section */}
                            <Layout.Section>
                                <Card roundedAbove="sm">
                                    <PurchaseOrderASNDetails asnId={asnId} parentData={parentData} poId={poId} />
                                </Card>
                            </Layout.Section>

                            {/* Cartons Table Section */}
                            <Layout.Section>
                                <Card sectioned>
                                    {cartonRows?.length ? (
                                        <CartonsTable
                                            productData={productData}
                                            skuToProduct={skuToProduct}
                                            cartonRows={cartonRows}
                                            activeModals={activeModals}
                                            toggleModal={toggleModal}
                                            cartonSortDirection={cartonSortHandlers.sortDirection}
                                            cartonSortColumnIndex={cartonSortHandlers.sortColumnIndex}
                                            handleSort={cartonSortHandlers.handleSort}
                                        />
                                    ) : (
                                        <Box padding="1000">
                                            <EmptySearchResult
                                                title={PURCHASE_ORDER_STRINGS.EMPTY_STATE.CARTONS_EMPTY_STATE_TITLE}
                                                description={PURCHASE_ORDER_STRINGS.EMPTY_STATE.CARTONS_EMPTY_STATE_DESCRIPTION}
                                                withIllustration
                                            />
                                        </Box>
                                    )}
                                </Card>
                            </Layout.Section>

                            {/* Receiving Documents Table Section */}
                            <Layout.Section>
                                <Card sectioned>
                                    {receivingDocumentRows?.length > 0 ? (
                                        <ReceivingDocumentsTable
                                            receivingDocumentRows={receivingDocumentRows}
                                            navigate={navigate}
                                            receivingDocumentSortDirection={receivingDocumentSortHandlers.sortDirection}
                                            receivingDocumentSortColumnIndex={receivingDocumentSortHandlers.sortColumnIndex}
                                            handleSort={receivingDocumentSortHandlers.handleSort}
                                            cartonRows={cartonRows}
                                            netsuiteId={parentData?.netsuiteId}
                                            isAuditRequired={parentData?.auditRequired}
                                            parentData={parentData}
                                            passedState={passedState}
                                        />
                                    ) : (
                                        <Box padding="1000">
                                            <EmptySearchResult
                                                title={PURCHASE_ORDER_STRINGS.EMPTY_STATE.RECEIVING_DOCUMENT_EMPTY_STATE_TITLE}
                                                description={PURCHASE_ORDER_STRINGS.EMPTY_STATE.RECEIVING_DOCUMENT_EMPTY_STATE_DESCRIPTION}
                                                withIllustration
                                            />
                                        </Box>
                                    )}
                                </Card>
                            </Layout.Section>
                        </>
                    ) : (
                        <Box padding="1000">
                            <EmptySearchResult
                                title={PURCHASE_ORDER_STRINGS.EMPTY_STATE.ASN_EMPTY_STATE_TITLE}
                                description={PURCHASE_ORDER_STRINGS.EMPTY_STATE.ASN_EMPTY_STATE_DESCRIPTION}
                                withIllustration
                            />
                        </Box>
                    )}
                </Layout>
            </Page>
        </>
    );
};

export default PurchaseOrderAsn;
